import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheoryProgress } from '../../hooks/useTheoryProgress';
import { useVideoProgress } from '../../hooks/useVideoProgress';

const LinkedLists: React.FC = () => {
  const navigate = useNavigate();
  useTheoryProgress('linkedList');
  const videoRef = useVideoProgress('linkedList');

  const handleBackClick = () => {
    navigate('/topics');
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Minimal grid background */}
      <div className="fixed inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:64px_64px] [background-position-x:center]"></div>
      
      <div className="relative z-10 max-w-5xl mx-auto px-4 py-8">
        {/* Header with back button */}
        <div className="flex items-center justify-between mb-8">
          <motion.button
            onClick={handleBackClick}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="text-sm font-medium">Back to Topics</span>
          </motion.button>
        </div>

        {/* Title and description */}
        <motion.div 
          className="mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-normal text-white mb-2">Linked Lists</h1>
          <p className="text-white/70 text-lg">Learn about linked list data structures and operations.</p>
        </motion.div>

        <div className="space-y-12">
          {/* Introduction */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">What Are Linked Lists?</h2>
            <div className="text-white/70 space-y-4">
              <p>
                Linked lists are dynamic data structures where elements are stored in nodes, 
                each pointing to the next node in the sequence. Unlike arrays, they don't require contiguous memory, 
                making them ideal for frequent insertions and deletions.
              </p>
              <p>
                Imagine a linked list as a treasure hunt: each clue (node) leads you to the next, 
                forming a chain that can grow or shrink as needed.
              </p>
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6 mt-6">
                <h3 className="text-xl font-semibold text-white mb-3">Core Characteristics</h3>
                <ul className="list-disc list-inside text-white/70 space-y-2">
                  <li><strong>Nodes:</strong> Store data and references</li>
                  <li><strong>Dynamic Size:</strong> Grows/shrinks at runtime</li>
                  <li><strong>Non-Contiguous:</strong> Scattered memory allocation</li>
                  <li><strong>Traversal:</strong> Sequential access only</li>
                </ul>
              </div>
            </div>
          </motion.section>

          {/* Types of Linked Lists */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-6">Types of Linked Lists</h2>
            <div className="space-y-6">
              {/* Singly Linked List */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Singly Linked List</h3>
                  <div className="text-2xl">➡️</div>
                </div>
                <p className="text-white/70 mb-4">
                  Each node points to the next, forming a one-way chain.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`class Node {
  data: number;
  next: Node | null;
  
  constructor(data: number) {
    this.data = data;
    this.next = null;
  }
}

class SinglyLinkedList {
  head: Node | null;
  
  constructor() {
    this.head = null;
  }
}

const list = new SinglyLinkedList();`}</code>
                  </pre>
                </div>
              </div>

              {/* Doubly Linked List */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Doubly Linked List</h3>
                  <div className="text-2xl">↔️</div>
                </div>
                <p className="text-white/70 mb-4">
                  Nodes point to both next and previous nodes, enabling bidirectional traversal.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`class Node {
  data: number;
  next: Node | null;
  prev: Node | null;
  
  constructor(data: number) {
    this.data = data;
    this.next = null;
    this.prev = null;
  }
}

class DoublyLinkedList {
  head: Node | null;
  tail: Node | null;
  
  constructor() {
    this.head = null;
    this.tail = null;
  }
}

const list = new DoublyLinkedList();`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Basic Operations */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-6">Basic Operations</h2>
            <div className="space-y-6">
              {/* Insertion */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Insertion</h3>
                  <div className="text-2xl">➕</div>
                </div>
                <p className="text-white/70 mb-4">
                  Adding nodes at the beginning or end of the list.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`class SinglyLinkedList {
  head: Node | null;
  constructor() { this.head = null; }
  
  // Insert at beginning: O(1)
  addFirst(data: number) {
    const newNode = new Node(data);
    newNode.next = this.head;
    this.head = newNode;
  }
  
  // Insert at end: O(n)
  addLast(data: number) {
    const newNode = new Node(data);
    if (!this.head) {
      this.head = newNode;
      return;
    }
    let current = this.head;
    while (current.next) {
      current = current.next;
    }
    current.next = newNode;
  }
}

const list = new SinglyLinkedList();
list.addFirst(1); // 1
list.addLast(2);  // 1 -> 2`}</code>
                  </pre>
                </div>
              </div>

              {/* Deletion */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Deletion</h3>
                  <div className="text-2xl">➖</div>
                </div>
                <p className="text-white/70 mb-4">
                  Removing nodes by position or value.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`class SinglyLinkedList {
  head: Node | null;
  constructor() { this.head = null; }
  
  // Delete first: O(1)
  deleteFirst() {
    if (!this.head) return;
    this.head = this.head.next;
  }
  
  // Delete by value: O(n)
  delete(data: number) {
    if (!this.head) return;
    if (this.head.data === data) {
      this.head = this.head.next;
      return;
    }
    let current = this.head;
    while (current.next) {
      if (current.next.data === data) {
        current.next = current.next.next;
        return;
      }
      current = current.next;
    }
  }
}

const list = new SinglyLinkedList();
list.addLast(1);
list.addLast(2);
list.delete(1); // 2`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Common Algorithms */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-6">Common Algorithms</h2>
            <div className="space-y-6">
              {/* Reverse a Linked List */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Reverse a Linked List</h3>
                  <div className="text-2xl">🔄</div>
                </div>
                <p className="text-white/70 mb-4">
                  Reverses the list by adjusting node pointers iteratively.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`class SinglyLinkedList {
  head: Node | null;
  constructor() { this.head = null; }
  
  reverse() {
    let prev: Node | null = null;
    let current = this.head;
    
    while (current) {
      const next = current.next;
      current.next = prev;
      prev = current;
      current = next;
    }
    
    this.head = prev;
  }
}

const list = new SinglyLinkedList();
list.addLast(1);
list.addLast(2);
list.addLast(3);
list.reverse(); // 3 -> 2 -> 1`}</code>
                  </pre>
                </div>
              </div>

              {/* Detect Cycle */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Detect Cycle</h3>
                  <div className="text-2xl">🔁</div>
                </div>
                <p className="text-white/70 mb-4">
                  Uses Floyd's Tortoise and Hare algorithm to detect cycles.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`class SinglyLinkedList {
  head: Node | null;
  constructor() { this.head = null; }
  
  hasCycle(): boolean {
    if (!this.head) return false;
    
    let slow = this.head;
    let fast = this.head;
    
    while (fast && fast.next) {
      slow = slow.next!;
      fast = fast.next.next;
      if (slow === fast) return true;
    }
    
    return false;
  }
}

// Example with cycle
const list = new SinglyLinkedList();
list.head = new Node(1);
list.head.next = new Node(2);
list.head.next.next = list.head; // Cycle
console.log(list.hasCycle()); // true`}</code>
                  </pre>
                </div>
              </div>

              {/* Merge Two Sorted Lists */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Merge Two Sorted Lists</h3>
                  <div className="text-2xl">🤝</div>
                </div>
                <p className="text-white/70 mb-4">
                  Combines two sorted linked lists into one sorted list.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`function mergeSortedLists(list1: Node | null, list2: Node | null): Node | null {
  if (!list1) return list2;
  if (!list2) return list1;
  
  let dummy = new Node(0);
  let current = dummy;
  
  while (list1 && list2) {
    if (list1.data <= list2.data) {
      current.next = list1;
      list1 = list1.next;
    } else {
      current.next = list2;
      list2 = list2.next;
    }
    current = current.next;
  }
  
  current.next = list1 || list2;
  return dummy.next;
}

// Example
const l1 = new SinglyLinkedList();
l1.addLast(1); l1.addLast(3);
const l2 = new SinglyLinkedList();
l2.addLast(2); l2.addLast(4);
const merged = mergeSortedLists(l1.head, l2.head); // 1 -> 2 -> 3 -> 4`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Time Complexity */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Time Complexity</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <p className="text-white/70 mb-4">
                Operation costs for singly linked lists:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-white/70">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="py-3 px-4 text-left">Operation</th>
                      <th className="py-3 px-4 text-left">Time Complexity</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-white/5">
                      <td className="py-3 px-4">Access by Index</td>
                      <td className="py-3 px-4">O(n)</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-3 px-4">Insert at Beginning</td>
                      <td className="py-3 px-4">O(1)</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-3 px-4">Insert at End</td>
                      <td className="py-3 px-4">O(n)</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-3 px-4">Delete at Beginning</td>
                      <td className="py-3 px-4">O(1)</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Delete at End</td>
                      <td className="py-3 px-4">O(n)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </motion.section>

          {/* Best Practices */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Best Practices</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <p className="text-white/70 mb-4">
                Guidelines for efficient linked list usage:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Do's</h3>
                  <ul className="list-disc list-inside text-white/70 space-y-2">
                    <li>Track head and tail for efficiency</li>
                    <li>Handle edge cases (empty/null)</li>
                    <li>Use dummy nodes for complex ops</li>
                    <li>Consider sentinel nodes</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Don'ts</h3>
                  <ul className="list-disc list-inside text-white/70 space-y-2">
                    <li>Don't break links unintentionally</li>
                    <li>Don't lose head reference</li>
                    <li>Don't ignore memory cleanup</li>
                    <li>Don't skip null checks</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Video Tutorial */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white/5 rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Video Tutorial</h2>
            <div className="aspect-w-16 aspect-h-9 bg-black/50 rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                >
                  <source src="https://files.catbox.moe/iyrmwk.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
            </div>
          </motion.section>

          {/* Why It Matters */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Why Linked Lists Matter</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <ul className="space-y-4 text-white/70">
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Flexibility:</strong> Dynamic resizing for unpredictable data.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Efficiency:</strong> O(1) inserts/deletes at known positions.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Foundation:</strong> Basis for stacks, queues, and more.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Real-World:</strong> Used in memory management and navigation.</span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Next Steps */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Next Steps</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <p className="text-white/70 mb-4">
                With linked list fundamentals, you can:
              </p>
              <ul className="list-disc list-inside text-white/70 space-y-2">
                <li>Implement list-based algorithms</li>
                <li>Optimize operations with pointers</li>
                <li>Explore advanced variants</li>
                <li>Practice real-world applications</li>
              </ul>
              <div className="mt-6 flex justify-center">
                <motion.button
                  onClick={() => navigate('/practice/linked-list')}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors flex items-center gap-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span>Start Practice</span>
                </motion.button>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default LinkedLists;